/**
 * GymRequestDetailPage Component
 *
 * Displays full details of a gym registration request.
 * If status is 'changes_requested', shows an editable form.
 * Shows admin notes and status prominently.
 */

import { useState, useEffect, type CSSProperties } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot, type DocumentData } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { GymRequestForm } from '@/components/gym-request/GymRequestForm';
import { GymRequestStatusBadge } from '@/components/gym-request/GymRequestStatusBadge';
import type { GymRequest, GymRequestStatus } from '@flash/shared';

/**
 * Convert Firestore document to GymRequest type
 */
const docToGymRequest = (docId: string, docData: DocumentData): GymRequest => {
  return {
    id: docId,
    requesterId: docData['requesterId'] as string,
    status: docData['status'] as GymRequestStatus,
    name: docData['name'] as string,
    description: docData['description'] as string,
    address: docData['address'] as string,
    city: docData['city'] as string,
    state: docData['state'] as string,
    country: docData['country'] as string,
    adminNotes: (docData['adminNotes'] as string | null) ?? null,
    reviewedBy: (docData['reviewedBy'] as string | null) ?? null,
    reviewedAt: docData['reviewedAt'] ?? null,
    gymId: (docData['gymId'] as string | null) ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * Format a timestamp for display
 */
const formatDate = (timestamp: unknown): string => {
  if (!timestamp) {
    return 'Unknown date';
  }

  // Handle Firestore Timestamp
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    const date = (timestamp as { toDate: () => Date }).toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return 'Unknown date';
};

/**
 * GymRequestDetailPage displays full details of a single gym request
 *
 * @example
 * ```tsx
 * // In router (wrapped in ProtectedRoute)
 * <Route path="/gym-requests/:id" element={<ProtectedRoute><GymRequestDetailPage /></ProtectedRoute>} />
 * ```
 */
export const GymRequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [request, setRequest] = useState<GymRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch request data with real-time updates
  useEffect(() => {
    if (!id || !user) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const requestRef = doc(db, 'gymRequests', id);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      requestRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const requestData = docToGymRequest(docSnapshot.id, docSnapshot.data());

          // Verify the current user owns this request
          if (requestData.requesterId !== user.uid) {
            setError('You do not have permission to view this request.');
            setRequest(null);
          } else {
            setRequest(requestData);
            setError(null);
          }
        } else {
          setError('Gym request not found.');
          setRequest(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching gym request:', err);
        setError('Failed to load gym request. Please try again.');
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [id, user]);

  /**
   * Handle successful form update
   */
  const handleUpdateSuccess = () => {
    setIsEditing(false);
  };

  /**
   * Handle cancel edit
   */
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  /**
   * Retry loading the request
   */
  const handleRetry = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const db = getFirebaseDb();
      const requestRef = doc(db, 'gymRequests', id);
      const docSnapshot = await getDoc(requestRef);

      if (docSnapshot.exists()) {
        const requestData = docToGymRequest(docSnapshot.id, docSnapshot.data());

        if (user && requestData.requesterId !== user.uid) {
          setError('You do not have permission to view this request.');
        } else {
          setRequest(requestData);
        }
      } else {
        setError('Gym request not found.');
      }
    } catch (err) {
      console.error('Error retrying gym request fetch:', err);
      setError('Failed to load gym request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading request details...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error</h2>
            <p style={errorTextStyle}>{error}</p>
            <div style={errorActionsStyle}>
              <button onClick={handleRetry} style={retryButtonStyle}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!request) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Request Not Found</h2>
            <p style={errorTextStyle}>The gym request you are looking for does not exist.</p>
          </div>
        </div>
      </main>
    );
  }

  const canEdit = request.status === 'changes_requested';
  const showEditForm = canEdit && isEditing;

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <div style={headerMainStyle}>
            <div style={titleContainerStyle}>
              <h1 style={titleStyle}>{request.name}</h1>
              <GymRequestStatusBadge status={request.status} style={statusBadgeStyle} />
            </div>
            <p style={locationStyle}>
              {request.city}, {request.state}, {request.country}
            </p>
          </div>
        </header>

        {/* Status-specific messages */}
        {request.status === 'pending' && (
          <div style={statusMessageStyle}>
            <div style={{ ...statusIconStyle, backgroundColor: '#fef3c7' }}>
              <span style={{ color: '#92400e' }}>!</span>
            </div>
            <div>
              <h3 style={statusMessageTitleStyle}>Awaiting Review</h3>
              <p style={statusMessageTextStyle}>
                Your request is in the queue and will be reviewed by an administrator soon.
              </p>
            </div>
          </div>
        )}

        {request.status === 'approved' && (
          <div
            style={{ ...statusMessageStyle, backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' }}
          >
            <div style={{ ...statusIconStyle, backgroundColor: '#d1fae5' }}>
              <span style={{ color: '#065f46', fontSize: '1.25rem' }}>&#10003;</span>
            </div>
            <div>
              <h3 style={statusMessageTitleStyle}>Request Approved</h3>
              <p style={statusMessageTextStyle}>
                Your gym has been created! You are now the owner.
                {request.gymId && (
                  <>
                    {' '}
                    <Link to={`/gyms/${request.gymId}`} style={viewGymLinkStyle}>
                      View your gym
                    </Link>
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {request.status === 'changes_requested' && !isEditing && (
          <div
            style={{ ...statusMessageStyle, backgroundColor: '#fff7ed', borderColor: '#fed7aa' }}
          >
            <div style={{ ...statusIconStyle, backgroundColor: '#ffedd5' }}>
              <span style={{ color: '#9a3412' }}>!</span>
            </div>
            <div style={statusMessageContentStyle}>
              <div>
                <h3 style={statusMessageTitleStyle}>Changes Requested</h3>
                <p style={statusMessageTextStyle}>
                  The administrator has requested some changes to your submission. Please review the
                  feedback below and update your request.
                </p>
              </div>
              <button onClick={() => setIsEditing(true)} style={editRequestButtonStyle}>
                Edit Request
              </button>
            </div>
          </div>
        )}

        {request.status === 'rejected' && (
          <div
            style={{ ...statusMessageStyle, backgroundColor: '#fef2f2', borderColor: '#fecaca' }}
          >
            <div style={{ ...statusIconStyle, backgroundColor: '#fee2e2' }}>
              <span style={{ color: '#991b1b', fontSize: '1.25rem' }}>&#10005;</span>
            </div>
            <div>
              <h3 style={statusMessageTitleStyle}>Request Rejected</h3>
              <p style={statusMessageTextStyle}>
                Unfortunately, your request was not approved. Please see the admin notes below for
                more information.
              </p>
            </div>
          </div>
        )}

        {/* Admin Notes */}
        {request.adminNotes && (
          <div style={adminNotesStyle}>
            <h2 style={sectionTitleStyle}>Admin Feedback</h2>
            <p style={adminNotesTextStyle}>{request.adminNotes}</p>
            {request.reviewedAt && (
              <p style={reviewedAtStyle}>Reviewed: {formatDate(request.reviewedAt)}</p>
            )}
          </div>
        )}

        {/* Edit Form */}
        {showEditForm && (
          <div style={editFormContainerStyle}>
            <h2 style={sectionTitleStyle}>Update Your Request</h2>
            <GymRequestForm
              mode="edit"
              initialData={{
                name: request.name,
                description: request.description,
                address: request.address,
                city: request.city,
                state: request.state,
                country: request.country,
              }}
              requestId={request.id}
              onSuccess={handleUpdateSuccess}
              onCancel={handleCancelEdit}
            />
          </div>
        )}

        {/* Request Details (when not editing) */}
        {!showEditForm && (
          <div style={detailsContainerStyle}>
            <h2 style={sectionTitleStyle}>Request Details</h2>

            <div style={detailSectionStyle}>
              <h3 style={detailLabelStyle}>Description</h3>
              <p style={detailValueStyle}>{request.description}</p>
            </div>

            <div style={detailSectionStyle}>
              <h3 style={detailLabelStyle}>Address</h3>
              <p style={detailValueStyle}>{request.address}</p>
              <p style={detailValueStyle}>
                {request.city}, {request.state}
              </p>
              <p style={detailValueStyle}>{request.country}</p>
            </div>

            <div style={metaContainerStyle}>
              <div style={metaItemStyle}>
                <span style={metaLabelStyle}>Submitted</span>
                <span style={metaValueStyle}>{formatDate(request.createdAt)}</span>
              </div>
              <div style={metaItemStyle}>
                <span style={metaLabelStyle}>Last Updated</span>
                <span style={metaValueStyle}>{formatDate(request.updatedAt)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
};

const contentStyle: CSSProperties = {
  maxWidth: '700px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: '#6b7280',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: '16px',
};

const errorActionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const headerMainStyle: CSSProperties = {};

const titleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '8px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: 0,
};

const statusBadgeStyle: CSSProperties = {
  fontSize: '0.875rem',
  padding: '6px 14px',
};

const locationStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  margin: 0,
};

const statusMessageStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
  padding: '16px',
  backgroundColor: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: '12px',
  marginBottom: '24px',
};

const statusIconStyle: CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontWeight: 700,
  fontSize: '1rem',
};

const statusMessageContentStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flex: 1,
  gap: '16px',
  flexWrap: 'wrap',
};

const statusMessageTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: '0 0 4px 0',
};

const statusMessageTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
  margin: 0,
  lineHeight: 1.5,
};

const viewGymLinkStyle: CSSProperties = {
  color: '#065f46',
  fontWeight: 500,
  textDecoration: 'underline',
};

const editRequestButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#f97316',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const adminNotesStyle: CSSProperties = {
  backgroundColor: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '24px',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: '0 0 12px 0',
};

const adminNotesTextStyle: CSSProperties = {
  fontSize: '0.9375rem',
  color: '#78350f',
  lineHeight: 1.6,
  margin: 0,
  whiteSpace: 'pre-wrap',
};

const reviewedAtStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#92400e',
  marginTop: '12px',
  marginBottom: 0,
};

const editFormContainerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

const detailsContainerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

const detailSectionStyle: CSSProperties = {
  marginBottom: '20px',
};

const detailLabelStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '0 0 6px 0',
};

const detailValueStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#1f2937',
  lineHeight: 1.6,
  margin: 0,
};

const metaContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '24px',
  flexWrap: 'wrap',
  paddingTop: '20px',
  borderTop: '1px solid #e5e7eb',
  marginTop: '20px',
};

const metaItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const metaLabelStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const metaValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#374151',
};

export default GymRequestDetailPage;
